// src/generators/dzial3_funkcje/temat3_kwadratowa/podtemat1_ogolna_types/FindQuadraticCoefficientsTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

/* =============================================================
   TASK: FindQuadraticCoefficientsTask
   Cel: odczytaj a, b, c z f(x) = ax^2 + bx + c
   Input: FractionInput użyty 3× (a, b, c)
============================================================= */

/* ---------- helpers ---------- */

function randInt(min, max, exclude = new Set()) {
  const span = max - min + 1;
  let v;
  do {
    v = Math.floor(Math.random() * span) + min;
  } while (exclude.has(v));
  return v;
}

function term(coeff, latexBase, opts = { omitOne: false }) {
  if (coeff === 0) return "";
  const sign = coeff > 0 ? " + " : " - ";
  const abs = Math.abs(coeff);
  const num = abs === 1 && opts.omitOne ? "" : String(abs);
  return `${sign}${num}${latexBase}`;
}

function formatQuadraticLatex(a, b, c) {
  const aCoeff = Math.abs(a) === 1 ? (a === -1 ? "-" : "") : String(a);
  let out = `${aCoeff}x^2`;
  out += term(b, "x", { omitOne: true });
  if (c !== 0) out += (c > 0 ? " + " : " - ") + String(Math.abs(c));
  return out;
}

// Parsowanie: "3/4", "-2 1/3", "1,25", "-4.5", "7"
function parseFractionLike(raw) {
  if (raw == null) return null;
  let s = String(raw).trim();
  if (!s) return null;

  s = s.replace(/\u2212/g, "-").replace(",", ".");

  // liczba mieszana A B/C
  let m = s.match(/^\s*([+-]?\d+)\s+(\d+)\s*\/\s*(\d+)\s*$/);
  if (m) {
    const A = parseInt(m[1], 10), B = parseInt(m[2], 10), C = parseInt(m[3], 10);
    if (!C) return null;
    const frac = B / C;
    return A >= 0 ? (A + frac) : (A - frac);
  }

  // zwykły ułamek P/Q
  m = s.match(/^\s*([+-]?\d+)\s*\/\s*([+-]?\d+)\s*$/);
  if (m) {
    const P = parseInt(m[1], 10), Q = parseInt(m[2], 10);
    if (!Q) return null;
    return P / Q;
  }

  // całkowita / dziesiętna
  if (/^[+-]?\d+(\.\d+)?$/.test(s)) return Number(s);
  return null;
}

function prettyRawToLatex(raw) {
  if (raw == null || String(raw).trim() === "") return "?";
  let s = String(raw).trim().replace(/\u2212/g, "-").replace(",", ".");
  // liczba mieszana
  let m = s.match(/^\s*([+-]?\d+)\s+(\d+)\s*\/\s*(\d+)\s*$/);
  if (m) return `${m[1]}\\;\\dfrac{${m[2]}}{${m[3]}}`;
  // zwykły ułamek
  m = s.match(/^\s*([+-]?\d+)\\?\/\\?(\d+)\s*$/);
  if (m) return `\\dfrac{${m[1]}}{${m[2]}}`;
  // dziesiętna -> przecinek
  if (/^[+-]?\d+\.\d+$/.test(s)) return s.replace(".", "{,}");
  return s;
}

/* ---------- API ---------- */

export function generateTask() {
  const a = randInt(-5, 5, new Set([0]));
  const b = randInt(-9, 9);
  const c = randInt(-9, 9);
  return { type: "FindQuadraticCoefficientsTask", data: { a, b, c } };
}

export function renderTask({ data }) {
  const { a, b, c } = data;
  const body = formatQuadraticLatex(a, b, c);

  return (
    <div style={{ marginTop: 6, marginBottom: 6 }}>
      <div>
        Dana jest funkcja w postaci ogólnej:{" "}
        <InlineMath math={'f(x) = ' + body} />
      </div>
      <div>
        Odczytaj <InlineMath math={'a'} />, <InlineMath math={'b'} /> oraz{" "}
        <InlineMath math={'c'} />.
      </div>
    </div>
  );
}

/* ---------- INPUT (jak w działającym zadaniu z 3 polami) ---------- */
export function renderInput(_task, userAnswer, onChange, disabled) {
  // dokładnie ten sam wzorzec co w FindAnglesFromTwoSidesAndOneAngleTask:
  const val = userAnswer ?? { a: { raw: "" }, b: { raw: "" }, c: { raw: "" } };

  const groupStyle = { display: "grid", gap: 6 };

  return (
    <div style={{ display: "grid", gap: 14, marginTop: 6 }}>
      <div style={groupStyle}>
        <InlineMath math={'a ='} />
        <FractionInput
          value={val.a}
          onChange={(v) => onChange({ ...(userAnswer ?? {}), a: v })}
          disabled={disabled}
          label="a"  // wewnętrzny „Podgląd” dostanie poprawną etykietę
        />
      </div>

      <div style={groupStyle}>
        <InlineMath math={'b ='} />
        <FractionInput
          value={val.b}
          onChange={(v) => onChange({ ...(userAnswer ?? {}), b: v })}
          disabled={disabled}
          label="b"
        />
      </div>

      <div style={groupStyle}>
        <InlineMath math={'c ='} />
        <FractionInput
          value={val.c}
          onChange={(v) => onChange({ ...(userAnswer ?? {}), c: v })}
          disabled={disabled}
          label="c"
        />
      </div>
    </div>
  );
}

/* ---------- CHECK ---------- */
export function checkAnswer({ data }, userAnswer) {
  try {
    if (!userAnswer) return false;

    const aRaw = userAnswer?.a?.raw ?? userAnswer?.a ?? "";
    const bRaw = userAnswer?.b?.raw ?? userAnswer?.b ?? "";
    const cRaw = userAnswer?.c?.raw ?? userAnswer?.c ?? "";

    const aParsed = parseFractionLike(aRaw);
    const bParsed = parseFractionLike(bRaw);
    const cParsed = parseFractionLike(cRaw);

    if ([aParsed, bParsed, cParsed].some(v => v == null || !Number.isFinite(v))) return false;

    return aParsed === data.a && bParsed === data.b && cParsed === data.c;
  } catch {
    return false;
  }
}

/* ---------- ANSWER ---------- */
export function renderAnswer({ data }) {
  const { a, b, c } = data;
  return (
    <div style={{ marginTop: 6, marginBottom: 6 }}>
      <InlineMath math={`a = ${a},\\quad b = ${b},\\quad c = ${c}`} />
    </div>
  );
}

/* ---------- EXPLANATION ---------- */
export function renderExplanation({ data }) {
  const { a, b, c } = data;
  const body = formatQuadraticLatex(a, b, c);
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7, marginTop: 8 }}>
      <div><strong>Krok 1.</strong> Postać ogólna:</div>
      <BlockMath math={'f(x)=ax^2+bx+c'} />
      <div><strong>Krok 2.</strong> Porównanie z podaną funkcją:</div>
      <BlockMath math={`f(x) = ${body}`} />
      <div><strong>Krok 3.</strong> Odczyt współczynników:</div>
      <BlockMath math={`a=${a},\\quad b=${b},\\quad c=${c}`} />
    </div>
  );
}

export function renderUserAnswer(arg1, arg2) {
  // Odporne na kolejność argumentów
  const looksLikeTask = (x) => x && typeof x === "object" && x.data && typeof x.data === "object";
  const task = looksLikeTask(arg1) ? arg1 : (looksLikeTask(arg2) ? arg2 : { data: {} });
  const userAnswer = looksLikeTask(arg1) ? arg2 : arg1;

  const raw = {
    a: userAnswer && (userAnswer.a?.raw ?? userAnswer.a) || "",
    b: userAnswer && (userAnswer.b?.raw ?? userAnswer.b) || "",
    c: userAnswer && (userAnswer.c?.raw ?? userAnswer.c) || "",
  };

  const empty = (s) => !String(s ?? "").trim();
  const allEmpty = empty(raw.a) && empty(raw.b) && empty(raw.c);

  if (allEmpty) {
    return <i>(brak odpowiedzi)</i>;
  }

  const show = (r) => {
    const s = String(r ?? "").trim();
    return s ? prettyRawToLatex(r) : "?";
  };

  return (
    <div style={{ marginTop: 6, marginBottom: 6 }}>
      <InlineMath math={`a = ${show(raw.a)}`} />
      <span style={{ margin: "0 10px" }} />
      <InlineMath math={`b = ${show(raw.b)}`} />
      <span style={{ margin: "0 10px" }} />
      <InlineMath math={`c = ${show(raw.c)}`} />
    </div>
  );
}



export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
